import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import api from '../lib/api';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [planInfo, setPlanInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const res = await api.get(`/api/subscriptions/verify?session_id=${sessionId}`);
        const data = res.data;

        if (data.status === 'success') {
          setStatus('success');
          setPlanInfo(data);
        } else if (data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="subscription-success-page">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Verifying your payment...</h1>
            <p className="text-sm text-gray-500">Please wait while we confirm your subscription.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Welcome to {planInfo?.plan_name}!</h1>
            <p className="text-sm text-gray-500 mb-6">
              Your subscription is now active. You've unlocked all {planInfo?.plan_name} features.
            </p>
            {planInfo?.price > 0 && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-2xl font-bold text-gray-900">${planInfo.price}<span className="text-sm font-normal text-gray-500">/month</span></p>
                <p className="text-xs text-gray-400 mt-1">{planInfo.tier === 'enterprise' ? 'Enterprise' : 'Professional'} Plan</p>
              </div>
            )}
            <button
              data-testid="go-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
            >
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {status === 'expired' ? 'Session Expired' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              {status === 'expired'
                ? 'Your checkout session has expired. Please try again.'
                : 'We couldn\'t verify your payment. If you were charged, don\'t worry — contact support and we\'ll sort it out.'}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-semibold text-sm transition-all"
            >
              Back to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
